import { type IframeHTMLAttributes, useEffect, useMemo, useRef } from "react";
import useScript from "@hooks/useScript";
import type { IframeResizeOptions } from "@typings/Cue";

type ResizableIframeProps = {
  /** Props for the underlying `<iframe>` (`ref` is always set by this component). */
  attributes?: IframeHTMLAttributes<HTMLIFrameElement>;
  /** Options for the iframe resizer. */
  options?: IframeResizeOptions;
};
/**
 * Resizeable iframe component that uses the iframe-resizer library to resize the iframe to the content height.
 */
const ResizableIframe = ({ attributes, options }: ResizableIframeProps) => {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const { isScriptReady } = useScript({
    id: "iframe-resizer",
    src: "/_web2/resizer/iframe-resizer.min.js",
    type: "text/javascript",
    checkScriptIsLoaded: () => typeof window.iFrameResize === "function",
  });

  const iframeOptions = useMemo(
    () => ({
      checkOrigin: false,
      log: true,
      heightCalculationMethod: "lowestElement" as const,
      ...options,
    }),
    [options]
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!isScriptReady) return;
    if (typeof window.iFrameResize !== "function") return;

    const iframeEl = iframeRef.current;
    if (!iframeEl) return;
    if (!attributes?.src) return;
    if (iframeEl.iFrameResizer) return;

    window.iFrameResize(iframeOptions, iframeEl);

    return () => {
      iframeEl.iFrameResizer?.removeListeners?.();
    };
  }, [isScriptReady, iframeOptions, attributes?.src]);

  if (!attributes?.src) return <></>;

  return (
    <iframe
      data-testid={attributes.id || "resizable-iframe-component"}
      ref={iframeRef}
      {...attributes}
      title={attributes.title ?? "Embedded content"}
      style={{
        width: "1px",
        minWidth: "100%",
        border: 0,
        ...attributes.style,
      }}
    />
  );
};

export default ResizableIframe;
